import { useEffect, useState } from "react";
import {
	Flex,
	Input,
	Text,
	Image,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalContent,
	ModalBody,
	ModalFooter,
	Button,
	useDisclosure,
	useToast,
	Heading,
	Spinner,
	Stack,
	InputGroup,
	InputLeftAddon,
	Textarea,
} from "@chakra-ui/react";
import UserInfo from "./UserInfo";
import FamilySection from "./FamilySection";
import InputWrapper from "../utils/InputWrapper";
import { formatInscripcionDate } from "../utils/utils";
import constanciaIcon from "../../img/diplomaIconWhite.png";
import { formHasError, isCellphoneValid } from "../utils/validators";
import { useSendSMS } from "../../data/useSendSms";
import { useDescargarConstancia } from "../../data/useDownloadFiles";
import {
	useSubmitInscripcion,
	useDeleteSubscription,
	useApproveInscripcion,
	useCorrectInscripcion,
} from "../../data/useSubmitInscripcion";
import FileManagerModal from "../utils/FileManagerModal";
import {
	useDeleteFile,
	useFetchFile,
	useUploadFile,
} from "../../data/useFileManager";
import { optimizeImageFile } from "../utils/fileOptimization";

export default function UserHome({
	userData,
	supervisorData,
	remeraImage,
	closeSession,
	params,
	refetchParams,
}) {
	const {
		isOpen: remeraModalOpen,
		onOpen: openRemeraModal,
		onClose: closeRemeraModal,
	} = useDisclosure();

	const {
		isOpen: anularInscripcionModalOpen,
		onOpen: openAnularInscripcionModal,
		onClose: closeAnularInscripcionModal,
	} = useDisclosure();

	const {
		isOpen: aprobarInscripcionModalOpen,
		onOpen: openAprobarInscripcionModal,
		onClose: closeAprobarInscripcionModal,
	} = useDisclosure();

	const {
		isOpen: corregirInscripcionModalOpen,
		onOpen: openCorregirInscripcionModal,
		onClose: closeCorregirInscripcionModal,
	} = useDisclosure();

	const {
		isOpen: tokenModalOpen,
		onOpen: openTokenModal,
		onClose: closeTokenModal,
	} = useDisclosure();

	const {
		isOpen: receiptModalOpen,
		onOpen: openReceiptModal,
		onClose: closeReceiptModal,
	} = useDisclosure();

	const {
		isOpen: partialSubmissionModalOpen,
		onOpen: openPartialSubmissionModal,
		onClose: closePartialSubmissionModal,
	} = useDisclosure();

	const toast = useToast();
	const { mutate: sendToken, isPending: isSendingSMS } = useSendSMS();
	const { mutate: submitInscripcion, isPending: isSubmittingInscripcion } =
		useSubmitInscripcion();
	const { mutate: deleteSubscription, isPending: isDeletingSubscription } =
		useDeleteSubscription();
	const { mutate: approveSubscricion, isPending: isApprovingSubscription } =
		useApproveInscripcion();
	const { mutate: correctInscription, isPending: isCorrectingInscription } =
		useCorrectInscripcion();
	const { mutate: descargarConstancia, isPending: isDownloadingConstancia } =
		useDescargarConstancia();

	const [dataInscripcion, setDataInscripcion] = useState(userData.inscripcion);
	const [prefix, setPrefix] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [email, setEmail] = useState(userData.correo);
	const [observaciones, setObservaciones] = useState(
		userData.inscripcion?.aclaraciones || ""
	);
	const [receiptInfo, setReceiptInfo] = useState(
		userData.inscripcion?.recibo
			? {
					url: userData.inscripcion.recibo,
					name: "Recibo",
			  }
			: null
	);
	const [token, setToken] = useState();
	const [mainFormFamilyMembers, setMainFormFamilyMembers] = useState(false);

	const { mutate: uploadReceipt, isPending: isUploadingReceipt } =
		useUploadFile();
	const { mutate: deleteReceipt, isPending: isDeletingReceipt } =
		useDeleteFile();
	const { mutate: fetchReceipt } = useFetchFile();

	const receiptIdentifier = userData?.documento
		? String(userData.documento)
		: "";

	useEffect(() => {
		if (!receiptIdentifier) {
			return;
		}

		fetchReceipt(
			{ type: "recibo", identifier: receiptIdentifier },
			{
				onSuccess: (response) => {
					if (response?.file) {
						setReceiptInfo(response.file);
					}
				},
			}
		);
	}, [fetchReceipt, receiptIdentifier]);

	const handleReceiptUpload = async (file) => {
		if (!receiptIdentifier) {
			toast({
				title: "No se pudo identificar al titular.",
				status: "warning",
				position: "top-center",
			});
			return;
		}

		const optimizedFile = await optimizeImageFile(file);

		const formData = new FormData();
		formData.append("action", "upload");
		formData.append("type", "recibo");
		formData.append("identifier", receiptIdentifier);
		formData.append("file", optimizedFile);

		uploadReceipt(formData, {
			onSuccess: (response) => {
				if (response?.file) {
					setReceiptInfo(response.file);
				}
				toast({
					title: "Recibo cargado correctamente.",
					status: "success",
					position: "top-center",
				});
				closeReceiptModal();
			},
			onError: () => {
				toast({
					title: "No se pudo cargar el recibo.",
					status: "error",
					position: "top-center",
				});
			},
		});
	};

	const handleReceiptDelete = () => {
		if (!receiptIdentifier) {
			return;
		}

		const formData = new FormData();
		formData.append("action", "delete");
		formData.append("type", "recibo");
		formData.append("identifier", receiptIdentifier);

		deleteReceipt(formData, {
			onSuccess: () => {
				setReceiptInfo(null);
				toast({
					title: "Recibo eliminado.",
					status: "info",
					position: "top-center",
				});
				closeReceiptModal();
			},
			onError: () => {
				toast({
					title: "No se pudo eliminar el recibo.",
					status: "error",
					position: "top-center",
				});
			},
		});
	};

	const handleReceiptView = () => {
		if (!receiptInfo) {
			return;
		}

		if (receiptInfo?.url) {
			window.open(receiptInfo.url, "_blank", "noopener,noreferrer");
		} else {
			window.open(
				"api/documentos/recibo/" + receiptInfo,
				"_blank",
				"noopener,noreferrer"
			);
		}
	};

	const handleAcceptPartialSubmission = () => {
		closePartialSubmissionModal();
		handleInscribir(true);
	};

	const handleInscribir = (acceptedPartialSubmission) => {
		const formData = formatFormData();
		const formError = formHasError(formData);
		if (formError) {
			return toast({
				title: formError,
				colorScheme: "yellow",
				position: "top-center",
				id: "phoneMessageError",
			});
		}
		if (supervisorData.isSupervisor || !formData.inscripcion.isValid) {
			// MANDAR EL SUBMIT

			if (!formData.inscripcion.isValid && !acceptedPartialSubmission) {
				return openPartialSubmissionModal();
			}

			openTokenModal();
			submitInscripcion(
				{
					inscripcionData: formData,
					tokenSeguridad: userData.tokenSeguridad,
				},
				{
					onSuccess: (data) => {
						if (data?.operacion?.includes("ALTA CORRECTA")) {
							toast({
								title: "Se registró la solicitud correctamente.",
								colorScheme: "green",
								position: "top-center",
								id: "phoneMessageError",
							});

							if (data?.inscripcion) {
								setDataInscripcion({
									...data?.inscripcion,
									movil: formData.movil,
								});
							}
							refetchParams();
						}
					},
					onError: () => {
						toast({
							title: "No se pudo generar la solicitud, intenta nuevamente.",
							colorScheme: "red",
							position: "top-center",
							id: "phoneMessageError",
						});
					},
					onSettled: () => {
						closeTokenModal();
					},
				}
			);
		} else if (isCellphoneValid(prefix + phoneNumber)) {
			let token = Math.floor(100000 + Math.random() * 900000);
			setToken(token);

			sendToken(
				{
					celular: prefix + phoneNumber,
					token: token,
					tokenSeguridad: userData.tokenSeguridad,
				},
				{
					onSuccess: (data) => {
						if (data.sms === "OK" && data.mensaje === "SMS Enviado") {
							openTokenModal();
						} else {
							if (!toast.isActive("phoneMessageError")) {
								toast({
									title:
										"Comprueba tu número de celular (Prefijo sin 0 + Número sin 15)",
									colorScheme: "red",
									position: "top-center",
									id: "phoneMessageError",
								});
							}
						}
					},
					onError: () => {},
				}
			);
		} else {
			if (!toast.isActive("phoneMessageError")) {
				toast({
					title:
						"Comprueba tu número de celular (Prefijo sin 0 + Número sin 15)",
					colorScheme: "yellow",
					position: "top-center",
					id: "phoneMessageError",
				});
			}
		}
	};

	const handleTokenChange = (e) => {
		if (token?.toString() === e.target.value) {
			const formData = formatFormData();

			submitInscripcion(
				{
					inscripcionData: formatFormData(),
					tokenSeguridad: userData.tokenSeguridad,
				},
				{
					onSuccess: (data) => {
						if (data?.operacion?.includes("ALTA CORRECTA")) {
							toast({
								title: "Se registró la solicitud correctamente.",
								colorScheme: "green",
								position: "top-center",
								id: "phoneMessageError",
							});

							if (data?.inscripcion) {
								setDataInscripcion({
									...data?.inscripcion,
									movil: formData.movil,
								});
							}
						}
					},
					onError: () => {
						toast({
							title: "No se pudo generar la solicitud, intenta nuevamente.",
							colorScheme: "red",
							position: "top-center",
							id: "phoneMessageError",
						});
					},
					onSettled: () => {
						closeTokenModal();
					},
				}
			);
		}
	};

	const handleDeleteSubscription = () => {
		deleteSubscription(
			{
				inscripcionData: { id_inscripcion: dataInscripcion?.id },
				tokenSeguridad: userData.tokenSeguridad,
			},
			{
				onSuccess: () => {
					toast({
						title: "Solicitud anulada correctamente.",
						colorScheme: "green",
						position: "top-center",
						id: "phoneMessageError",
					});
					closeSession();
					refetchParams();
				},
				onError: () => {
					toast({
						title: "No se pudo anular la solicitud, intenta nuevamente.",
						colorScheme: "red",
						position: "top-center",
						id: "phoneMessageError",
					});
				},
				onSettled: () => {
					closeAnularInscripcionModal();
				},
			}
		);
	};

	const handleApproveSubscription = () => {
		approveSubscricion(
			{
				inscripcionData: { id_inscripcion: dataInscripcion?.id },
				tokenSeguridad: userData.tokenSeguridad,
			},
			{
				onSuccess: () => {
					toast({
						title: "Solicitud aprobada.",
						colorScheme: "green",
						position: "top-center",
						id: "phoneMessageError",
					});
					closeSession();
				},
				onError: () => {
					toast({
						title: "No se pudo aprobar la solicitud, intenta nuevamente.",
						colorScheme: "red",
						position: "top-center",
						id: "phoneMessageError",
					});
				},
				onSettled: () => {
					closeAprobarInscripcionModal();
				},
			}
		);
	};

	const handleCorrectSubscription = () => {
		const formData = formatFormData();
		formData.inscripcion.tipo_inscripcion = "CORREGIR";

		correctInscription(
			{
				inscripcionData: formData,
				tokenSeguridad: userData.tokenSeguridad,
			},
			{
				onSuccess: () => {
					toast({
						title: "Se mandó a corregir la inscripción.",
						colorScheme: "green",
						position: "top-center",
						id: "phoneMessageError",
					});
					closeSession();
				},
				onError: () => {
					toast({
						title:
							"No se pudo mandar a corregir la inscripción, intenta nuevamente.",
						colorScheme: "red",
						position: "top-center",
						id: "phoneMessageError",
					});
				},
				onSettled: () => {
					closeCorregirInscripcionModal();
				},
			}
		);
	};

	const formatFormData = () => {
		const formData = {
			evento: params.evento,
			id_socio: userData.id_socio,
			id_persona: userData.id_persona,
			movil: prefix + phoneNumber,
			correo: email,
			tokenSeguridad: userData.tokenSeguridad,
			inscripcion: formatInscripcion(observaciones),
			supervisor: supervisorData.isSupervisor
				? supervisorData.user.documento
				: "",
			observaciones,
			recibo: receiptInfo?.url || dataInscripcion?.recibo || "",
		};

		return formData;
	};

	const formatInscripcion = (observaciones) => {
		const participantes = (mainFormFamilyMembers || []).map((fm) => {
			return {
				...fm,
				id_persona: fm.id_persona,
				documento: fm.documento,
				nivel: fm.nivel || "",
				grado: fm.grado || "",
				certificado:
					(typeof fm.certificado === "object"
						? fm.certificado?.url
						: fm.certificado) || "",
			};
		});
		const isValid =
			participantes.every((p) => !!p.certificado && p.nivel && p.grado) &&
			!!(receiptInfo?.url || dataInscripcion?.recibo);

		return {
			...dataInscripcion,
			aclaraciones: observaciones,
			participantes,
			recibo: receiptInfo?.url || dataInscripcion?.recibo || "",
			tipo_inscripcion: isValid ? "COMPLETA" : "INCOMPLETA",
			isValid,
		};
	};

	const zoomOutMobile = () => {
		var viewport = document.querySelector('meta[name="viewport"]');

		if (viewport) {
			viewport.content = "initial-scale=1";
			viewport.content = "width=device-width";
		}
	};

	const downloadPDF = async (isDeslinde) => {
		descargarConstancia(
			{
				idInscripcion: dataInscripcion.id,
				tokenSeguridad: userData.tokenSeguridad,
				isDeslinde: isDeslinde,
			},
			{
				onSuccess: (downloadData) => {
					if (downloadData) {
						const blob = new Blob([downloadData], {
							type: "application/pdf",
						});

						window.open(URL.createObjectURL(blob), "_blank").document.title =
							isDeslinde ? "Deslinde" : "Constancia de Inscipción";
					} else {
						alert("error");
					}
				},
				onError: () => {
					toast({
						title:
							"No se pudo descargar el archivo, intenta nuevamente o contacta a mesa de ayuda.",
						colorScheme: "red",
						position: "top-center",
						id: "descargarError",
					});
				},
			}
		);
	};

	const disableFields =
		dataInscripcion?.observacion &&
		dataInscripcion?.observacion !== "NO ENVIADA";

	const canSubmit =
		!dataInscripcion?.id || dataInscripcion?.observacion === "NO ENVIADA";

	return (
		<Flex
			bg={"#f9fad2"}
			flex={1}
			flexDir="column"
			alignItems={["center", "flex-start"]}
			p={[5, 10]}
			gap={3}
		>
			{isDownloadingConstancia && (
				<Flex
					opacity={"0.6"}
					zIndex={99999}
					bg="#444"
					position={"fixed"}
					w="100vw"
					h="100vh"
					top={0}
					placeContent={"center"}
					alignItems={"center"}
				>
					<Spinner color="white" size="xl" />
				</Flex>
			)}
			<UserInfo userdata={userData} />
			{!userData.estado_observacion && (
				<>
					{dataInscripcion?.id && (
						<>
							<Text
								fontSize={16}
								fontWeight={600}
								w={["100%", "400px"]}
								textAlign={"center"}
							>
								Solicitud N° {dataInscripcion?.numero} -{" "}
								{formatInscripcionDate(dataInscripcion?.momento)}
							</Text>
							{dataInscripcion?.observacion && (
								<Text
									fontSize={16}
									fontWeight={600}
									w={["100%", "400px"]}
									textAlign={"center"}
									color={
										dataInscripcion?.observacion === "APROBADA"
											? "#60bd60"
											: dataInscripcion?.observacion === "ENVIADA"
											? "#7a7a7a"
											: "red"
									}
								>
									{dataInscripcion?.observacion}
								</Text>
							)}
							{dataInscripcion?.id &&
								(["APROBADA", "ENVIADA"].includes(
									dataInscripcion?.observacion
								) ||
									supervisorData.isSupervisor) && (
									<Flex
										w={["100%", "400px"]}
										fontSize={14}
										fontWeight={600}
										justifyContent={"space-between"}
									>
										<Flex
											alignItems={"center"}
											onClick={() => downloadPDF()}
											cursor={"pointer"}
										>
											<Flex
												h="40px"
												w="40px"
												bg="#ECC94B"
												alignItems={"center"}
												justify={"center"}
												borderRadius={"50%"}
												mr="10px"
											>
												<Image src={constanciaIcon} h={"28px"} />
											</Flex>
											<Text> Constancia de Solicitud</Text>
										</Flex>
									</Flex>
								)}
						</>
					)}

					<InputWrapper label={"Celular"}>
						{dataInscripcion?.id && !canSubmit ? (
							<Stack
								direction={["column", "row"]}
								alignItems={["flex-start", "center"]}
								spacing={3}
								px={1}
							>
								<Input
									borderColor="#333"
									type="number"
									value={dataInscripcion.movil ?? userData?.movil}
									fontWeight="600"
									placeholder={"Celular"}
									bg={"#FAFAFA"}
									disabled={disableFields}
									sx={{
										_disabled: {
											opacity: 0.9,
										},
									}}
								/>
								<Button
									leftIcon={
										<Text as="span" fontSize="lg">
											📎
										</Text>
									}
									colorScheme={receiptInfo ? "green" : "yellow"}
									variant={receiptInfo ? "solid" : "outline"}
									onClick={openReceiptModal}
									size="sm"
								>
									Recibo
								</Button>
							</Stack>
						) : (
							<Stack alignItems="center" direction="row" w="100%" spacing={2}>
								<InputGroup w="">
									<InputLeftAddon
										padding="5px"
										display="flex"
										alignItems="center"
										justifyContent="center"
										w="20px"
										children="0"
										borderColor={"#333"}
										fontWeight={600}
									/>
									<Input
										bg={"#FAFAFA"}
										w="50px"
										p="0 5px"
										borderColor="#333"
										type="tel"
										maxLength="4"
										value={prefix}
										onChange={(e) => {
											setPrefix(e.target.value);
										}}
										disabled={disableFields}
										sx={{
											_disabled: {
												opacity: 0.9,
											},
										}}
										fontWeight="600"
									/>
								</InputGroup>
								<InputGroup>
									<InputLeftAddon
										padding="5px"
										display="flex"
										alignItems="center"
										justifyContent="center"
										w="40px"
										children="15"
										borderColor={"#333"}
										fontWeight={600}
									/>
									<Input
										bg={"#FAFAFA"}
										borderColor="#333"
										type="tel"
										p="0 5px"
										maxLength="8"
										value={phoneNumber}
										onChange={(e) => {
											setPhoneNumber(e.target.value);
										}}
										disabled={disableFields}
										sx={{
											_disabled: {
												opacity: 0.9,
											},
										}}
										fontWeight="600"
									/>
								</InputGroup>
								<Button
									leftIcon={
										<Text as="span" fontSize="lg">
											📎
										</Text>
									}
									colorScheme={receiptInfo ? "green" : "yellow"}
									variant={receiptInfo ? "solid" : "outline"}
									onClick={openReceiptModal}
									size="sm"
									px={5}
								>
									Recibo
								</Button>
							</Stack>
						)}
					</InputWrapper>

					<InputWrapper label={"Correo"}>
						<Input
							borderColor="#333"
							type="email"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
							}}
							fontWeight="600"
							placeholder={"Correo"}
							bg={"#FAFAFA"}
							disabled={disableFields}
							sx={{
								_disabled: {
									opacity: 0.9,
								},
							}}
						/>
					</InputWrapper>

					<FamilySection
						userData={userData}
						disableFields={disableFields}
						setMainFormFamilyMembers={setMainFormFamilyMembers}
						params={params}
						readOnly={["ENVIADA", "APROBADA"].includes(
							dataInscripcion?.observacion
						)}
						isSupervisor={supervisorData.isSupervisor}
						supervisorDNI={supervisorData.user?.documento}
					/>

					<InputWrapper label={"Observaciones"}>
						<Textarea
							borderColor="#333"
							type="text"
							value={observaciones}
							onChange={(e) => {
								setObservaciones(e.target.value);
							}}
							fontWeight="600"
							bg={"#FAFAFA"}
							disabled={disableFields}
							placeholder={"Observaciones"}
							sx={{
								_disabled: {
									opacity: 0.9,
								},
							}}
							rows={6}
						/>
					</InputWrapper>

					<Flex
						direction={["column", "row"]}
						gap={4}
						mt={4}
						w={"100%"}
						justifyContent={"center"}
					>
						{(canSubmit || supervisorData.isSupervisor) && (
							<Button
								mt={5}
								colorScheme={!canSubmit ? "red" : "yellow"}
								_hover={
									!dataInscripcion?.id && {
										bg: "#ecc94b",
									}
								}
								onClick={
									!canSubmit
										? openAnularInscripcionModal
										: () => handleInscribir(false)
								}
								alignSelf="center"
								w={["65%", 250]}
								isLoading={isSendingSMS}
								isDisabled={
									!dataInscripcion?.id &&
									dataInscripcion.participantes?.length === 0
								}
							>
								{!canSubmit ? "Anular" : "Inscribir"}
							</Button>
						)}

						{dataInscripcion?.id &&
							supervisorData.isSupervisor &&
							["PROVISORIA", "ENVIADA"].includes(
								dataInscripcion?.observacion
							) && (
								<>
									<Button
										mt={5}
										colorScheme={"green"}
										_hover={
											!dataInscripcion?.id && {
												bg: "#ecc94b",
											}
										}
										onClick={openAprobarInscripcionModal}
										alignSelf="center"
										w={["65%", 250]}
										isLoading={isSendingSMS}
										isDisabled={
											!dataInscripcion?.id &&
											dataInscripcion.participantes?.length === 0
										}
									>
										Aprobar
									</Button>
									<Button
										mt={5}
										colorScheme={"orange"}
										_hover={
											!dataInscripcion?.id && {
												bg: "#ecc94b",
											}
										}
										onClick={openCorregirInscripcionModal}
										alignSelf="center"
										w={["65%", 250]}
										isLoading={isSendingSMS}
										isDisabled={
											!dataInscripcion?.id &&
											dataInscripcion.participantes?.length === 0
										}
									>
										Corregir
									</Button>
								</>
							)}
					</Flex>
				</>
			)}

			<Button
				mt={5}
				colorScheme="yellow"
				_hover={{
					bg: "#ecc94b",
				}}
				onClick={closeSession}
				alignSelf="center"
				w={["65%", 250]}
			>
				Volver
			</Button>

			{remeraModalOpen && (
				<Modal
					isOpen={remeraModalOpen}
					onClose={() => {
						closeRemeraModal();
						zoomOutMobile();
					}}
					blockScrollOnMount={false}
				>
					<ModalOverlay />
					<ModalContent
						mx={5}
						paddingTop={6}
						minWidth="fit-content"
						height="fit-content"
					>
						<ModalCloseButton />
						<ModalBody>
							<Image src={remeraImage} />
						</ModalBody>
					</ModalContent>
				</Modal>
			)}

			{anularInscripcionModalOpen && (
				<Modal
					isOpen={anularInscripcionModalOpen}
					onClose={closeAnularInscripcionModal}
				>
					<ModalOverlay>
						<ModalContent
							mx={5}
							paddingTop={7}
							minWidth="fit-content"
							marginTop="15vh"
						>
							<ModalCloseButton />
							<ModalBody>
								<Heading fontWeight="semibold" size="md">
									Estás seguro que deseas anular la solicitud?
								</Heading>
							</ModalBody>
							<ModalFooter>
								<Button
									variant="ghost"
									mr={3}
									onClick={closeAnularInscripcionModal}
								>
									Cancelar
								</Button>
								<Button
									colorScheme="red"
									isLoading={isDeletingSubscription}
									onClick={handleDeleteSubscription}
								>
									Anular
								</Button>
							</ModalFooter>
						</ModalContent>
					</ModalOverlay>
				</Modal>
			)}

			{aprobarInscripcionModalOpen && (
				<Modal
					isOpen={aprobarInscripcionModalOpen}
					onClose={closeAprobarInscripcionModal}
				>
					<ModalOverlay>
						<ModalContent
							mx={5}
							paddingTop={7}
							minWidth="fit-content"
							marginTop="15vh"
						>
							<ModalCloseButton />
							<ModalBody>
								<Heading fontWeight="semibold" size="md">
									Estás seguro que deseas aprobar la solicitud?
								</Heading>
							</ModalBody>
							<ModalFooter>
								<Button
									variant="ghost"
									mr={3}
									onClick={closeAprobarInscripcionModal}
								>
									Cancelar
								</Button>
								<Button
									colorScheme="green"
									isLoading={isApprovingSubscription}
									onClick={handleApproveSubscription}
								>
									Aprobar
								</Button>
							</ModalFooter>
						</ModalContent>
					</ModalOverlay>
				</Modal>
			)}
			{corregirInscripcionModalOpen && (
				<Modal
					isOpen={corregirInscripcionModalOpen}
					onClose={closeCorregirInscripcionModal}
				>
					<ModalOverlay>
						<ModalContent
							mx={5}
							paddingTop={7}
							minWidth="fit-content"
							marginTop="15vh"
						>
							<ModalCloseButton />
							<ModalBody>
								<Heading fontWeight="semibold" size="md">
									Se mandará a corregir la inscripción. Estás seguro?
								</Heading>
							</ModalBody>
							<ModalFooter>
								<Button
									variant="ghost"
									mr={3}
									onClick={closeCorregirInscripcionModal}
								>
									Cancelar
								</Button>
								<Button
									colorScheme="green"
									isLoading={isCorrectingInscription}
									onClick={handleCorrectSubscription}
								>
									Corregir
								</Button>
							</ModalFooter>
						</ModalContent>
					</ModalOverlay>
				</Modal>
			)}

			{partialSubmissionModalOpen && (
				<Modal
					isOpen={partialSubmissionModalOpen}
					onClose={closePartialSubmissionModal}
				>
					<ModalOverlay>
						<ModalContent
							mx={5}
							paddingTop={7}
							minWidth="fit-content"
							marginTop="15vh"
						>
							<ModalCloseButton />
							<ModalBody>
								<Heading fontWeight="semibold" size="md">
									La Solicitud se grabara pero no sera enviada a ASOEM hasta que
									no complete toda la información
								</Heading>
							</ModalBody>
							<ModalFooter>
								<Button
									variant="ghost"
									mr={3}
									onClick={closePartialSubmissionModal}
								>
									Cancelar
								</Button>
								<Button
									colorScheme="green"
									onClick={handleAcceptPartialSubmission}
								>
									Aceptar
								</Button>
							</ModalFooter>
						</ModalContent>
					</ModalOverlay>
				</Modal>
			)}

			{tokenModalOpen && (
				<Modal
					blockScrollOnMount={false}
					isOpen={tokenModalOpen}
					onClose={() => {
						closeTokenModal();
					}}
				>
					<ModalOverlay padding={5}>
						<ModalContent marginTop="5vh" minH="300px" marginX={5}>
							<ModalCloseButton />
							<ModalBody
								display="flex"
								flexDirection="column"
								alignItems="center"
								textAlign="center"
								justifyContent="space-around"
								mt="20px"
							>
								{isSubmittingInscripcion ? (
									<Flex flexDir="column" alignItems="center">
										<Heading>Enviando tus datos</Heading>
										<Spinner mt={10} />
									</Flex>
								) : (
									<>
										<Heading size="md">
											Ingresa el código de 6 dígitos que se envió al número{" "}
											{prefix + phoneNumber}
										</Heading>
										<Input
											placeholder="123456"
											textAlign="center"
											maxLength="6"
											onChange={handleTokenChange}
										/>
										<Heading fontWeight="semibold" size="xs">
											Si el número es incorrecto o después de unos minutos no te
											llegó el código, vuelve atrás y presiona Aceptar
											nuevamente
										</Heading>
										<Button
											onClick={() => {
												closeTokenModal();
											}}
										>
											Volver
										</Button>
									</>
								)}
							</ModalBody>
						</ModalContent>
					</ModalOverlay>
				</Modal>
			)}

			<FileManagerModal
				isOpen={receiptModalOpen}
				onClose={closeReceiptModal}
				title="Recibo"
				description="Subí una foto o PDF del recibo."
				hasFile={Boolean(receiptInfo)}
				onUpload={handleReceiptUpload}
				onView={handleReceiptView}
				onDelete={handleReceiptDelete}
				isUploading={isUploadingReceipt}
				isDeleting={isDeletingReceipt}
				uploadLabel="Subir recibo"
				viewLabel="Ver recibo"
				deleteLabel="Eliminar recibo"
				allowUpload={!disableFields}
				allowDelete={!disableFields}
			/>
		</Flex>
	);
}
